import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import AppHeader from "@/components/ui/AppHeader";
import { api } from "@/lib/api";
import { getCardById, getCardImage, FORMAT_LABELS, DEFAULT_FORMAT, DECK_FORMATS } from "@dm-online/shared";
import type { DeckFormat } from "@dm-online/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Typography } from "@/components/ui/typography";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DeckListItem from "@/components/ui/DeckListItem";

interface DeckSummary {
  id: string;
  name: string;
  card_ids: string[];
  key_card_id?: string | null;
  format?: DeckFormat;
  // 共有用デッキコード。他のアカウントに渡すとデッキ作成画面でインポートできる
  share_code?: string | null;
  // 自由記述メモ（本人用）
  memo?: string;
}

// フォーマット絞り込みの「すべて」を表す番兵値（Select は空文字を許容しないため）
const ALL_FORMATS = "all";

export default function DecksPage() {
  const navigate = useNavigate();

  const [decks, setDecks] = useState<DeckSummary[]>([]);
  const [deckLoading, setDeckLoading] = useState(true);

  // 絞り込み条件
  const [query, setQuery] = useState("");
  const [formatFilter, setFormatFilter] = useState<DeckFormat | typeof ALL_FORMATS>(ALL_FORMATS);

  const fetchDecks = async () => {
    const res = await api.get<DeckSummary[]>("/decks");
    if (res.ok && res.data) setDecks(res.data);
    setDeckLoading(false);
  };

  useEffect(() => {
    fetchDecks();
  }, []);

  const deleteDeck = async (id: string) => {
    if (!confirm("このデッキを削除しますか？")) return;
    await api.delete(`/decks/${id}`);
    setDecks((prev) => prev.filter((d) => d.id !== id));
  };

  // デッキ名・メモ（部分一致・大文字小文字無視）とフォーマットで絞り込む
  const visibleDecks = useMemo(() => {
    const q = query.trim().toLowerCase();
    return decks.filter((deck) => {
      const matchesQuery =
        q === "" || deck.name.toLowerCase().includes(q) || (deck.memo ?? "").toLowerCase().includes(q);
      const matchesFormat =
        formatFilter === ALL_FORMATS || (deck.format ?? DEFAULT_FORMAT) === formatFilter;
      return matchesQuery && matchesFormat;
    });
  }, [decks, query, formatFilter]);

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="mx-auto max-w-5xl space-y-5 p-6">
        <div className="flex items-center justify-between">
          <Typography variant="h2" as="h1">デッキリスト</Typography>
          <Button size="sm" onClick={() => navigate("/deck")} className="gap-1.5">
            <Plus />
            デッキ作成
          </Button>
        </div>

        {deckLoading ? (
          <Typography tone="muted" className="py-10 text-center">読み込み中...</Typography>
        ) : decks.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border py-12 text-center">
            <Typography tone="muted">デッキがありません</Typography>
            <Button size="sm" className="mt-3 gap-1.5" onClick={() => navigate("/deck")}>
              <Plus />
              最初のデッキを作る
            </Button>
          </div>
        ) : (
          <>
            {/* 絞り込み：デッキ名検索 + フォーマット */}
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="デッキ名で検索"
                  className="pl-8"
                />
              </div>
              <Select
                value={formatFilter}
                onValueChange={(v) => setFormatFilter(v as DeckFormat | typeof ALL_FORMATS)}
              >
                <SelectTrigger className="w-full sm:w-56" title="フォーマットで絞り込む">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FORMATS}>すべてのフォーマット</SelectItem>
                  {DECK_FORMATS.map((f) => (
                    <SelectItem key={f} value={f}>{FORMAT_LABELS[f]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {visibleDecks.length === 0 ? (
              <Typography tone="muted" className="py-10 text-center">
                条件に一致するデッキがありません
              </Typography>
            ) : (
              <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {visibleDecks.map((deck) => {
                  const kc = deck.key_card_id
                    ? getCardById(deck.key_card_id)
                    : undefined;
                  // key_card_id は正規ID。デッキ内で同カードに選ばれている刷りID を探し、
                  // そのイラストを一覧にも反映する（無ければ既定イラスト）
                  const keyPrintId = deck.key_card_id
                    ? deck.card_ids.find((id) => (getCardById(id)?.id ?? id) === deck.key_card_id)
                    : undefined;
                  return (
                    <li key={deck.id}>
                      <DeckListItem
                        name={deck.name}
                        formatLabel={FORMAT_LABELS[deck.format ?? DEFAULT_FORMAT]}
                        keyCardImage={keyPrintId ? getCardImage(keyPrintId) : kc?.imageUrl}
                        keyCardName={kc?.name}
                        onOpen={() => navigate(`/deck/${deck.id}`)}
                        onDelete={() => deleteDeck(deck.id)}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </main>
    </div>
  );
}
